'use client';

import { memo, useCallback } from 'react';

import { type Clip } from '@air/api/types';
import { ChevronDownIcon, LayersIcon } from '@air/design-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';

import { VersionItem } from '~/components/VersionItem';

export type VersionSelectorProps = {
  /** All available versions for the asset */
  versions: Clip[];
  /** Currently selected version number */
  selectedVersion: number;
  /** Callback when a version is selected */
  onVersionSelect: (clip: Clip) => void;
  /** When true, the selector is non-interactive and a tooltip explains why. */
  disabled?: boolean;
  /** Tooltip text shown on hover when `disabled`. */
  disabledTooltip?: string;
};

/**
 * Dropdown selector for switching between asset versions.
 * Shows a trigger with the current version number and a dropdown list of all versions.
 * Each item displays a thumbnail preview, title, and metadata.
 * Only renders when there are multiple versions.
 */
export const VersionSelector = memo(
  ({ versions, selectedVersion, onVersionSelect, disabled = false, disabledTooltip }: VersionSelectorProps) => {
    const handleSelect = useCallback(
      (clip: Clip) => {
        onVersionSelect(clip);
      },
      [onVersionSelect],
    );

    if (versions.length <= 1) {
      return null;
    }

    if (disabled) {
      const trigger = (
        <button
          aria-disabled
          aria-label="Select version"
          className="flex cursor-not-allowed items-center gap-1.5 rounded-8 bg-neutral-4 p-1 px-2 font-medium text-12 text-neutral-9 outline-none dark:bg-neutral-6"
          disabled
          type="button"
        >
          <LayersIcon size="sm" />
          <span>V{selectedVersion}</span>
          <ChevronDownIcon size="xs" />
        </button>
      );

      return disabledTooltip ? <Tooltip label={disabledTooltip}>{trigger}</Tooltip> : trigger;
    }

    return (
      <DropdownMenu
        align="start"
        sideOffset={4}
        size="large"
        trigger={
          <button
            aria-label="Select version"
            className="flex items-center gap-1.5 rounded-8 bg-neutral-4 p-1 px-2 font-medium text-12 text-neutral-12 outline-none hover:bg-neutral-5 focus:ring-2 focus:ring-brand-9 dark:bg-neutral-6 dark:hover:bg-neutral-7"
            type="button"
          >
            <LayersIcon size="sm" />
            <span>V{selectedVersion}</span>
            <ChevronDownIcon size="xs" />
          </button>
        }
      >
        {versions.map((version) => (
          <VersionItem
            clip={version}
            isSelected={version.version === selectedVersion}
            key={version.id}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onSelect={() => handleSelect(version)}
          />
        ))}
      </DropdownMenu>
    );
  },
);

VersionSelector.displayName = 'VersionSelector';
